import classNames from 'classnames';
import { IconEye, IconInfo, IconPrinter } from 'featherico';
import { compose, map, reject } from 'lodash/fp';
import Button from '../../../../../components/Button';
import UserDateTime from '../../../../../components/DateTime/UserDateTime';
import LinkButton from '../../../../../components/LinkButton';
import { Option, Select } from '../../../../../components/Select';
import { usePrint } from '../../../../../hooks/usePrint';
import { trackEvent } from '../../../../../services/Tracking';
import { getViewUrl } from '../../../services/url';
import type { Review } from '../../../types';
import { ConfidentialQuestionToggle } from './ConfidentialQuestionToggle';
import styles from './style.m.less';

export const VIEW_LAYOUTS = {
    STACKED: { type: 'STACKED', name: 'Stacked', layout: 'STACKED', showReviewee: true, showReviewer: true },
    SIDE_BY_SIDE: {
        type: 'SIDE_BY_SIDE',
        name: 'Side by side',
        layout: 'COLUMNS',
        showReviewee: true,
        showReviewer: true,
    },
    REVIEWEE_ONLY: {
        type: 'REVIEWEE_ONLY',
        name: 'Reviewee only',
        layout: 'STACKED',
        showReviewee: true,
        showReviewer: false,
    },
    REVIEWER_ONLY: {
        type: 'REVIEWER_ONLY',
        name: 'Reviewer only',
        layout: 'STACKED',
        showReviewee: false,
        showReviewer: true,
    },
    HIDE_BOTH: {
        type: 'HIDE_BOTH',
        name: 'Hide both',
        layout: 'STACKED',
        showReviewee: false,
        showReviewer: false,
    },
} as const;

export type ViewLayout = (typeof VIEW_LAYOUTS)[keyof typeof VIEW_LAYOUTS];

export interface ConfidentialQuestionsToggle {
    showConfidentialQuestions: boolean;
    toggleConfidentialQuestions: () => void;
    showConfidentialQuestionsButton: boolean;
}

interface PageHeaderProps {
    title?: string;
    cycleName?: string;
    completedAt?: string | null;
    viewLayout: ViewLayout;
    onSelectViewLayout?: (viewLayout: ViewLayout['type']) => void;
    showInfoAndTimeline: boolean;
    toggleInfoAndTimeline: () => void;
    confidentialQuestionsToggle: ConfidentialQuestionsToggle;
}

interface CardHeaderProps {
    title: string;
    completedAt?: string | null;
    review: Pick<Review, 'id' | 'complete'>;
    showPrintButton?: boolean;
}

export default function PageHeader({
    title,
    cycleName,
    completedAt,
    viewLayout,
    onSelectViewLayout,
    confidentialQuestionsToggle: {
        showConfidentialQuestions,
        toggleConfidentialQuestions,
        showConfidentialQuestionsButton,
    },
    showInfoAndTimeline,
    toggleInfoAndTimeline,
}: PageHeaderProps) {
    return (
        <header className={classNames(styles.header, { [styles.paddingBottom]: !onSelectViewLayout })}>
            {title && (
                <div className={styles.containerHeading}>
                    <h1 className={styles.heading}>{title}</h1>
                    {cycleName && (
                        <p className={styles.subTitle}>
                            {cycleName}
                            {completedAt && <FormattedDate label=" · Completed" date={completedAt} />}
                        </p>
                    )}
                </div>
            )}
            <div className={styles.pageHeaderButtons}>
                {showConfidentialQuestionsButton && (
                    <ConfidentialQuestionToggle
                        showConfidentialQuestions={showConfidentialQuestions}
                        toggleConfidentialQuestions={toggleConfidentialQuestions}
                    />
                )}
                <ViewLayoutSelect viewLayout={viewLayout} onSelectViewLayout={onSelectViewLayout} />
                <PrintButton />
                <InfoAndTimelineToggle
                    toggleInfoAndTimeline={toggleInfoAndTimeline}
                    showInfoAndTimeline={showInfoAndTimeline}
                />
            </div>
        </header>
    );
}

const ViewLayoutSelect = ({
    viewLayout,
    onSelectViewLayout,
}: {
    viewLayout: ViewLayout;
    onSelectViewLayout?: (viewLayout: ViewLayout['type']) => void;
}) => {
    const optionToText = (layout: ViewLayout | null) => (layout ? layout.name : '');

    return onSelectViewLayout ? (
        <Select
            className={styles.viewLayoutSelect}
            selected={viewLayout}
            onSelect={(selected: ViewLayout) => {
                trackEvent({ category: 'review title bar', action: 'select view layout', label: selected.name });
                return onSelectViewLayout(selected.type);
            }}
            optionToText={optionToText}
        >
            {compose(
                map((layout: ViewLayout) => (
                    <Option key={layout.type} value={layout}>
                        {layout.name}
                    </Option>
                )),
                reject(['type', 'HIDE_BOTH'])
            )(VIEW_LAYOUTS)}
        </Select>
    ) : null;
};

const PrintButton = () => {
    const print = usePrint();

    return (
        <Button
            className={styles.pageHeaderButton}
            onClick={print}
            eventTracking={{ category: 'review title bar', action: 'print' }}
            kind="TRANSPARENT"
            aria-label="Print"
        >
            <IconPrinter />
        </Button>
    );
};

const InfoAndTimelineToggle = ({
    toggleInfoAndTimeline,
    showInfoAndTimeline,
}: {
    toggleInfoAndTimeline: () => void;
    showInfoAndTimeline: boolean;
}) => (
    <Button
        onClick={toggleInfoAndTimeline}
        className={styles.pageHeaderButton}
        eventTracking={{
            category: 'review title bar',
            action: 'toggle timeline',
            label: showInfoAndTimeline ? 'hide' : 'show',
        }}
        kind={showInfoAndTimeline ? 'PRIMARY_ALTERNATE' : 'TRANSPARENT'}
        aria-label={`${showInfoAndTimeline ? 'Hide' : 'Show'} info and timeline information`}
    >
        <IconInfo />
    </Button>
);

export function CardHeader({ title, completedAt, review, showPrintButton = true }: CardHeaderProps) {
    return (
        <header
            className={classNames(styles.cardHeader, {
                [styles.completeRibbonOffset]: review.complete,
            })}
        >
            <div>
                <h1 className={styles.heading}>{title}</h1>
                <p className={styles.subTitle}>
                    <FormattedDate label="Completed" date={completedAt ?? null} />
                </p>
            </div>
            <div>{showPrintButton && <PrintViewButton review={review} />}</div>
        </header>
    );
}

function PrintViewButton({ review }: { review: Pick<Review, 'id'> }) {
    return (
        <LinkButton className={styles.cardHeaderButton} href={getViewUrl(review.id)}>
            <IconEye /> Print View
        </LinkButton>
    );
}

function FormattedDate({ label, date }: { label: string; date: string | null }) {
    return date ? (
        <span className={styles.date}>
            {label} on <UserDateTime date={date} format="shortDate" />
        </span>
    ) : null;
}
